"use client";

// ── The Rehearsal tab (`EX-TST01`, `docs/29` §4.1) ──────────────────────────
// What the round's staff tried before it opened: who, which problem, which
// language, the verdict and the first failing test. It exists only for the
// staff and the round's moderators (the tab is not drawn for anybody else and
// the route refuses them), and it stays after the start as the record.
//
// A row opens to every test's verdict and time, the `full` policy, which is the
// one fact a tester came for ("C fails on test 7"). Never the test data, and
// never anybody's source.

import { useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { formatNumber } from "@/lib/format-datetime";
import {
  SubmissionResultDetail,
  VerdictChip,
} from "@/features/judge/components/workspace/SubmissionResult";
import { formatRuntimeLanguageName } from "@/features/quiz/lib/coding-task-format";
import { useContestRehearsal, useContestRehearsals } from "../hooks";
import { formatDateTime } from "../lib/format";
import { useContestDisplayZone } from "../lib/display-zone";
import type { ContestRehearsalItem } from "../types";

function RehearsalDetail({ slug, item }: { slug: string; item: ContestRehearsalItem }) {
  const t = useTranslations("contest.rehearsal");
  const tJudge = useTranslations("judge");
  const detail = useContestRehearsal(slug, item.submissionId);

  return (
    <div className="border-t border-[rgb(var(--border))] bg-neutral-50/60 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[rgb(var(--muted-foreground))]">
        <span className="tabular-nums">
          {tJudge("scoreValue", { score: item.score, maxScore: item.maxScore })}
        </span>
        {item.totalCount > 0 ? (
          <span className="tabular-nums">
            {tJudge("hiddenSummary", { passed: item.passedCount, total: item.totalCount })}
          </span>
        ) : null}
      </div>
      <div className="mt-2">
        {detail.isPending ? (
          <Skeleton className="h-6 w-48" />
        ) : detail.data ? (
          <SubmissionResultDetail result={detail.data} />
        ) : (
          <p className="text-[11px] text-red-700">{t("detailFailed")}</p>
        )}
      </div>
      <p className="mt-2 text-[11px] text-[rgb(var(--muted-foreground))]">{t("dataNote")}</p>
    </div>
  );
}

export function ContestRehearsalPanel({ slug, locale }: { slug: string; locale: string }) {
  const t = useTranslations("contest.rehearsal");
  const tPage = useTranslations("contest.page");
  const zone = useContestDisplayZone();
  const rehearsals = useContestRehearsals(slug);
  const [openRow, setOpenRow] = useState<string | null>(null);

  if (rehearsals.isPending) {
    return <Skeleton className="h-48 w-full rounded-xl" />;
  }

  if (rehearsals.isError || !rehearsals.data) {
    return (
      <p className="rounded-xl border border-[rgb(var(--border))] bg-white p-8 text-center text-sm text-[rgb(var(--muted-foreground))]">
        {t("loadFailed")}
      </p>
    );
  }

  const { items, total, canRehearse } = rehearsals.data;

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm leading-relaxed text-neutral-800">
        <span className="font-semibold">{t("staffOnly")}</span> {t("intro")}
        {canRehearse ? null : <span className="mt-1 block text-neutral-600">{t("closed")}</span>}
      </div>

      {items.length === 0 ? (
        <p className="rounded-xl border border-[rgb(var(--border))] bg-white p-8 text-center text-sm text-[rgb(var(--muted-foreground))]">
          {canRehearse ? t("empty") : t("emptyClosed")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[rgb(var(--border))] bg-white">
          {items.map((item) => {
            const open = openRow === item.submissionId;
            return (
              <div key={item.submissionId} className="border-b border-[rgb(var(--border))] last:border-b-0">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => setOpenRow(open ? null : item.submissionId)}
                  className="flex w-full flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3 text-left transition-colors hover:bg-neutral-50"
                >
                  <span className="w-6 shrink-0 text-sm font-bold text-neutral-900">
                    {item.letter ?? "-"}
                  </span>
                  <span className="min-w-0 max-w-[14rem] truncate text-sm font-medium text-neutral-900">
                    {item.mine ? t("you") : (item.name ?? "-")}
                  </span>
                  {item.role ? (
                    <span className="rounded-md border border-[rgb(var(--border))] px-1.5 py-0.5 text-[11px] text-neutral-600">
                      {tPage(`staffRole_${item.role}`)}
                    </span>
                  ) : null}
                  <VerdictChip status={item.status} verdict={item.verdict ?? null} />
                  {item.failedTest !== null ? (
                    <span className="text-[11px] text-neutral-600">
                      {t("failedOn", { test: item.failedTest })}
                    </span>
                  ) : null}
                  <span className="font-mono text-[11px] text-[rgb(var(--muted-foreground))]">
                    {formatRuntimeLanguageName(item.languageKey)}
                    <span className="text-neutral-400">{` ${item.languageVersion}`}</span>
                  </span>
                  {item.timeMs !== undefined ? (
                    <span className="text-[11px] tabular-nums text-[rgb(var(--muted-foreground))]">
                      {`${item.timeMs} ms`}
                    </span>
                  ) : null}
                  <span className="ml-auto text-[11px] tabular-nums text-[rgb(var(--muted-foreground))]">
                    {formatDateTime(item.createdAt, locale, zone)}
                  </span>
                  <ChevronDown
                    className={cn("h-4 w-4 shrink-0 text-neutral-400 transition-transform", open && "rotate-180")}
                    aria-hidden
                  />
                </button>
                {open ? <RehearsalDetail slug={slug} item={item} /> : null}
              </div>
            );
          })}
        </div>
      )}

      {total > items.length ? (
        <p className="text-[11px] text-[rgb(var(--muted-foreground))]">
          {t("newestOf", { shown: formatNumber(items.length, locale), total: formatNumber(total, locale) })}
        </p>
      ) : null}
    </div>
  );
}
